import { Check } from "lucide-react";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import {
  AI_SCRIPT_RERUN_VERSION_CHOICE,
  type AiScriptRerunVersionChoice,
} from "./types";

interface RerunOptionCardProps {
  title: string;
  description: string;
  selected: boolean;
  onSelect: () => void;
}

const RerunOptionCard = ({
  title,
  description,
  selected,
  onSelect,
}: RerunOptionCardProps) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={cn(
      "flex w-full items-start gap-3 rounded-lg border bg-card px-4 py-3 text-left transition-colors",
      "hover:border-stroke-primary hover:bg-surface-secondary",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
      {
        "border-stroke-primary bg-surface-secondary": selected,
        "border-input": !selected,
      },
    )}
  >
    <span
      className={cn(
        "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border transition-colors",
        {
          "border-primary bg-primary text-primary-foreground": selected,
          "border-stroke-primary": !selected,
        },
      )}
    >
      {selected && <Check className="h-3 w-3" />}
    </span>
    <Stack gap="sm" className="min-w-0">
      <Typography variant="subtitleW3">{title}</Typography>
      <Typography variant="captionW1" textColor="tertiary">
        {description}
      </Typography>
    </Stack>
  </button>
);

interface RerunVersionSelectorProps {
  label: string;
  runVersion: number | null;
  currentVersion: number | null;
  choice: AiScriptRerunVersionChoice | null;
  onChange: (choice: AiScriptRerunVersionChoice) => void;
}

export const RerunVersionSelector = ({
  label,
  runVersion,
  currentVersion,
  choice,
  onChange,
}: RerunVersionSelectorProps) => (
  <Stack gap="sm" fullWidth>
    <Typography variant="subtitleW3">{label} version</Typography>
    <Stack
      gap="sm"
      fullWidth
      role="radiogroup"
      aria-label={`Which ${label.toLowerCase()} version to run with`}
    >
      <RerunOptionCard
        title="Keep last run's"
        description={`v${runVersion}`}
        selected={choice === AI_SCRIPT_RERUN_VERSION_CHOICE.keep}
        onSelect={() => onChange(AI_SCRIPT_RERUN_VERSION_CHOICE.keep)}
      />
      <RerunOptionCard
        title="Use latest"
        description={`v${currentVersion}`}
        selected={choice === AI_SCRIPT_RERUN_VERSION_CHOICE.latest}
        onSelect={() => onChange(AI_SCRIPT_RERUN_VERSION_CHOICE.latest)}
      />
    </Stack>
  </Stack>
);

export const RerunVersionUnchanged = ({
  label,
  version,
}: {
  label: string;
  version: number;
}) => (
  <Typography variant="captionW1" textColor="tertiary">
    {label} v{version} · unchanged
  </Typography>
);
